/**
 * Picks the file manager documents whose pages are appended to the IAP.
 *
 * Scoped to the two places the export can read from: this incident's workspace
 * and the organization's. Files from another incident are deliberately absent
 * rather than selectable-then-dropped, because the server refuses them and a
 * silent omission in a printed plan is worse than not offering the file at
 * all.
 */

import React, { useMemo, useState } from 'react';

import { useGetAllFiles } from 'components/FileManagement/hooks/useGetAllFiles';
import type {
  FileOrFolder,
  NotebookFile,
} from 'components/FileManagement/hooks/useGetAllFiles';
import { WORKSPACE_TYPES } from '../../../../../types/workspace';
import {
  selectGroup,
  selectGroupGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import {
  canAppendToIap,
  UNSUPPORTED_ATTACHMENT_REASON,
} from 'components/IAP/iapFileAttachmentSupport';
import type { IapFileAttachment } from 'components/IAP/iapAttachmentManifest';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

type IapFileAttachmentPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incidentId: string;
  /** Already-chosen files, so reopening the picker shows the current selection. */
  selected: IapFileAttachment[];
  /** How many more rows the cover can print, counting the current selection. */
  maxSelectable: number;
  onConfirm: (attachments: IapFileAttachment[]) => void;
};

type PickableFile = {
  id: string;
  name: string;
  size: string;
  supported: boolean;
};

type FileGroup = {
  key: string;
  label: string;
  files: PickableFile[];
};

function toPickableFiles(dataset: FileOrFolder[] | undefined): PickableFile[] {
  return (dataset ?? [])
    .filter((item): item is NotebookFile => item.type === 'file')
    .map((file) => ({
      id: file.id,
      name: file.file_name,
      size: file.size ?? '',
      supported: canAppendToIap(file.mimetype, file.file_name),
    }))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
}

const IapFileAttachmentPicker: React.FC<IapFileAttachmentPickerProps> = ({
  open,
  onOpenChange,
  incidentId,
  selected,
  maxSelectable,
  onConfirm,
}) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const orgGroup = useAppSelector(selectGroup);

  const { data: incidentFiles } = useGetAllFiles(open ? incidentId : undefined);
  const { data: orgFiles } = useGetAllFiles(
    open ? groupGuid : undefined,
    WORKSPACE_TYPES.ORG
  );

  const [searchTerm, setSearchTerm] = useState('');
  // Seeded from the saved selection each time the dialog opens, so cancelling
  // leaves the manifest alone.
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraftIds(selected.map((attachment) => attachment.fileId));
      setSearchTerm('');
    }
  }

  const groups = useMemo<FileGroup[]>(() => {
    const needle = searchTerm.trim().toLowerCase();
    const match = (files: PickableFile[]) =>
      needle.length === 0
        ? files
        : files.filter((file) => file.name.toLowerCase().includes(needle));

    return [
      {
        key: 'incident',
        label: 'This incident',
        files: match(toPickableFiles(incidentFiles?.dataset)),
      },
      {
        key: 'organization',
        label: orgGroup?.group_name || 'Organization',
        files: match(toPickableFiles(orgFiles?.dataset)),
      },
    ].filter((group) => group.files.length > 0);
  }, [incidentFiles, orgFiles, orgGroup, searchTerm]);

  const namesById = useMemo(() => {
    const names = new Map<string, string>();
    for (const group of groups) {
      for (const file of group.files) names.set(file.id, file.name);
    }
    return names;
  }, [groups]);

  const atLimit = draftIds.length >= maxSelectable;

  const toggle = (fileId: string) => {
    setDraftIds((previous) =>
      previous.includes(fileId)
        ? previous.filter((id) => id !== fileId)
        : [...previous, fileId]
    );
  };

  const handleConfirm = () => {
    // The label defaults to the file's own name; a name we no longer have in
    // hand comes off the existing selection rather than being blanked.
    const existing = new Map(
      selected.map((attachment) => [attachment.fileId, attachment.label])
    );
    onConfirm(
      draftIds.map((fileId) => ({
        fileId,
        label: namesById.get(fileId) ?? existing.get(fileId) ?? '',
      }))
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark tw:max-w-2xl tw:text-foreground">
        <DialogHeader>
          <DialogTitle>Attach files to the IAP</DialogTitle>
          <DialogDescription>
            Selected files are named on the cover and their pages are appended
            to the end of the plan. The cover has room for {maxSelectable}{' '}
            {maxSelectable === 1 ? 'attachment' : 'attachments'}.
          </DialogDescription>
        </DialogHeader>

        <Input
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search files"
          aria-label="Search files"
          data-testid="iap-attachment-search"
        />

        <div className="tw:max-h-96 tw:overflow-y-auto tw:rounded-md tw:border tw:border-border">
          {groups.length === 0 ? (
            <p className="tw:p-4 tw:text-sm tw:text-muted-foreground">
              No files found. Upload one in File Management first.
            </p>
          ) : (
            groups.map((group) => (
              <section key={group.key}>
                <h4 className="tw:sticky tw:top-0 tw:bg-muted tw:px-3 tw:py-1.5 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                  {group.label}
                </h4>
                <ul>
                  {group.files.map((file) => {
                    const checked = draftIds.includes(file.id);
                    const disabled = !file.supported || (atLimit && !checked);
                    return (
                      <li
                        key={file.id}
                        className="tw:flex tw:items-center tw:gap-3 tw:border-t tw:border-border tw:px-3 tw:py-2"
                      >
                        <Checkbox
                          id={`iap-attachment-${file.id}`}
                          checked={checked}
                          disabled={disabled}
                          onCheckedChange={() => toggle(file.id)}
                        />
                        <label
                          htmlFor={`iap-attachment-${file.id}`}
                          className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col"
                        >
                          <span className="tw:truncate tw:text-sm">
                            {file.name}
                          </span>
                          <span className="tw:text-xs tw:text-muted-foreground">
                            {file.supported
                              ? file.size
                              : UNSUPPORTED_ATTACHMENT_REASON}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </div>

        <DialogFooter>
          <span className="tw:mr-auto tw:text-sm tw:text-muted-foreground">
            {draftIds.length} of {maxSelectable} selected
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>Attach</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IapFileAttachmentPicker;
